import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Users, CheckCircle, Clock, Warning, MagnifyingGlass,
  ArrowUp, ArrowDown, Buildings, FileText, RocketLaunch,
  Bell, EnvelopeSimple, Gear, Lightning, PaperPlaneTilt
} from '@phosphor-icons/react';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { PageHero } from '../components/motion/PageHero';
import api, {
  getOnboardingNotifications, getOnboardingNotifSettings,
  updateOnboardingNotifSettings, testOnboardingNotifications
} from '../services/api';

const STATUS_CONFIG = {
  completed: { label: 'Termine', badge: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  in_progress: { label: 'En cours', badge: 'bg-amber-100 text-amber-700', icon: Clock },
  pending: { label: 'Non demarre', badge: 'bg-gray-100 text-gray-500', icon: Warning },
};

const STEP_LABELS = {
  adhesion: 'Adhesion',
  documents: 'Documents',
  validation: 'Validation',
  activation: 'Activation',
};

const EVENT_TYPE_LABELS = {
  status_change: 'Changement statut',
  missing_documents: 'Documents manquants',
  missing_documents_cron: 'Relance auto',
};

const NOTIF_STATUS_BADGES = {
  sent: 'bg-emerald-100 text-emerald-700',
  mock: 'bg-blue-100 text-blue-700',
  error: 'bg-red-100 text-red-700',
};

export default function OnboardingOverviewPage() {
  const [activeTab, setActiveTab] = useState('members');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('progress_asc');
  const [expandedMember, setExpandedMember] = useState(null);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notifStats, setNotifStats] = useState(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [testRunning, setTestRunning] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/onboarding/overview');
        setData(res.data);
      } catch {
        toast.error('Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const loadNotifications = async () => {
    setNotifLoading(true);
    try {
      const [notifRes, settingsRes] = await Promise.all([
        getOnboardingNotifications({ limit: 50 }),
        getOnboardingNotifSettings(),
      ]);
      setNotifications(notifRes.data.items || []);
      setNotifStats(notifRes.data.stats || {});
      setSettings(settingsRes.data);
    } catch {
      toast.error('Erreur chargement notifications');
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'notifications') loadNotifications();
  }, [activeTab]);

  const handleToggleSetting = async (key) => {
    if (!settings) return;
    const newVal = !settings[key];
    setSettingsLoading(true);
    try {
      const res = await updateOnboardingNotifSettings({ [key]: newVal });
      setSettings(res.data);
      toast.success('Parametre mis a jour');
    } catch {
      toast.error('Erreur mise a jour');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleTestScan = async () => {
    setTestRunning(true);
    try {
      const res = await testOnboardingNotifications();
      toast.success(`Scan termine: ${res.data.sent} email(s) envoye(s), ${res.data.skipped} ignore(s)`);
      loadNotifications();
    } catch {
      toast.error('Erreur scan');
    } finally {
      setTestRunning(false);
    }
  };

  const filteredMembers = useMemo(() => {
    if (!data) return [];
    let list = [...data.members];

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        m.raison_sociale.toLowerCase().includes(q) ||
        (m.territoire || '').toLowerCase().includes(q)
      );
    }

    if (filterStatus !== 'all') {
      list = list.filter(m => m.status === filterStatus);
    }

    if (sortBy === 'progress_asc') list.sort((a, b) => a.global_progress - b.global_progress);
    else if (sortBy === 'progress_desc') list.sort((a, b) => b.global_progress - a.global_progress);
    else if (sortBy === 'name') list.sort((a, b) => a.raison_sociale.localeCompare(b.raison_sociale));

    return list;
  }, [data, search, filterStatus, sortBy]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  if (!data) return null;

  const { stats } = data;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="onboarding-overview">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Suivi Onboarding FTPE"
        subtitle="Progression et taux de complétion des membres"
        icon={<RocketLaunch size={24} weight="duotone" className="text-white" />}
        gradient="from-[#5E6D66] to-[#2D5A4C]"
        accents={['#FF8E00', '#80C51F']}
      />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E8E6E1]">
        {[
          { key: 'members', label: 'Membres', icon: Users },
          { key: 'notifications', label: 'Notifications', icon: Bell },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`tab-${tab.key}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-[#2D5A4C] text-[#2D5A4C]'
                : 'border-transparent text-[#5E6D66] hover:text-[#1A2421]'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Members Tab */}
      {activeTab === 'members' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="onboarding-stats">
            <Card className="border-[#E8E6E1]">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-[#2D5A4C]/10 rounded-lg"><Users size={20} className="text-[#2D5A4C]" /></div>
                <div>
                  <p className="text-2xl font-bold text-[#1A2421]" data-testid="stat-total">{stats.total_members}</p>
                  <p className="text-xs text-[#5E6D66]">Total</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-[#E8E6E1]">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg"><CheckCircle size={20} className="text-emerald-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-[#1A2421]" data-testid="stat-completed">{stats.completed}</p>
                  <p className="text-xs text-[#5E6D66]">Termines</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-[#E8E6E1]">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg"><Clock size={20} className="text-amber-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-[#1A2421]" data-testid="stat-in-progress">{stats.in_progress}</p>
                  <p className="text-xs text-[#5E6D66]">En cours</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-[#E8E6E1]">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg"><Warning size={20} className="text-gray-500" /></div>
                <div>
                  <p className="text-2xl font-bold text-[#1A2421]" data-testid="stat-pending">{stats.pending}</p>
                  <p className="text-xs text-[#5E6D66]">Non demarres</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-[#E8E6E1]">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg"><RocketLaunch size={20} className="text-blue-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-[#1A2421]" data-testid="stat-avg">{stats.avg_progress}%</p>
                  <p className="text-xs text-[#5E6D66]">Progression moy.</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 max-w-xs">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
              <Input
                placeholder="Rechercher organisation..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-white border-[#E8E6E1]"
                data-testid="onboarding-search"
              />
            </div>
            <div className="flex gap-1.5">
              {[
                { key: 'all', label: 'Tous' },
                { key: 'pending', label: 'Non demarres' },
                { key: 'in_progress', label: 'En cours' },
                { key: 'completed', label: 'Termines' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setFilterStatus(f.key)}
                  data-testid={`filter-${f.key}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    filterStatus === f.key
                      ? 'bg-[#2D5A4C] text-white'
                      : 'bg-white text-[#5E6D66] border border-[#E8E6E1] hover:bg-[#F9F8F5]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setSortBy(prev => prev === 'progress_asc' ? 'progress_desc' : 'progress_asc')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-white text-[#5E6D66] border border-[#E8E6E1] hover:bg-[#F9F8F5] transition-colors"
              data-testid="sort-toggle"
            >
              {sortBy === 'progress_asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
              Progression
            </button>
          </div>

          {/* Members List */}
          <div className="space-y-2" data-testid="members-list">
            {filteredMembers.length === 0 ? (
              <Card className="border-[#E8E6E1]">
                <CardContent className="p-8 text-center">
                  <Buildings size={40} className="mx-auto text-[#E8E6E1] mb-3" />
                  <p className="text-[#5E6D66]">Aucun membre trouve</p>
                </CardContent>
              </Card>
            ) : (
              filteredMembers.map((member) => {
                const isExpanded = expandedMember === member.organization_id;
                const sc = STATUS_CONFIG[member.status];

                return (
                  <Card key={member.organization_id} className="border-[#E8E6E1]" data-testid={`member-card-${member.organization_id}`}>
                    <CardContent className="p-0">
                      <button
                        className="w-full flex items-center gap-4 p-4 hover:bg-[#F9F8F5] transition-colors text-left"
                        onClick={() => setExpandedMember(isExpanded ? null : member.organization_id)}
                      >
                        {/* Mini progress circle */}
                        <div className="relative flex-shrink-0">
                          <svg width="44" height="44" viewBox="0 0 44 44">
                            <circle cx="22" cy="22" r="18" fill="none" stroke="#E8E6E1" strokeWidth="4" />
                            <circle
                              cx="22" cy="22" r="18" fill="none"
                              stroke={member.global_progress === 100 ? '#10b981' : '#2D5A4C'}
                              strokeWidth="4" strokeLinecap="round"
                              strokeDasharray={`${2 * Math.PI * 18}`}
                              strokeDashoffset={`${2 * Math.PI * 18 * (1 - member.global_progress / 100)}`}
                              transform="rotate(-90 22 22)"
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-[10px] font-bold text-[#1A2421]">{member.global_progress}%</span>
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-[#1A2421] truncate">{member.raison_sociale}</p>
                            {member.membre_ftpe && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">FTPE</Badge>}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            {member.territoire && <span className="text-xs text-[#5E6D66]">{member.territoire}</span>}
                            <span className="text-xs text-[#5E6D66]">{member.dossiers_count} dossier{member.dossiers_count > 1 ? 's' : ''}</span>
                            <span className="text-xs text-[#5E6D66]">{member.required_docs_checked}/{member.required_docs_total} docs</span>
                          </div>
                        </div>

                        <Badge className={sc.badge}>{sc.label}</Badge>

                        {isExpanded ? <ChevronUp size={16} className="text-[#5E6D66]" /> : <ChevronDown size={16} className="text-[#5E6D66]" />}
                      </button>

                      {isExpanded && member.steps && (
                        <div className="px-4 pb-4 border-t border-[#E8E6E1]">
                          <div className="pt-3 flex gap-2">
                            {member.steps.map((step) => {
                              const stepStyle = STATUS_CONFIG[step.status] || STATUS_CONFIG.pending;
                              return (
                                <div key={step.key} className="flex-1">
                                  <div className={`h-1.5 rounded-full mb-1 ${
                                    step.status === 'completed' ? 'bg-emerald-400' :
                                    step.status === 'in_progress' ? 'bg-amber-400' : 'bg-gray-200'
                                  }`} />
                                  <p className="text-[11px] font-medium text-[#1A2421]">{STEP_LABELS[step.key]}</p>
                                  <p className="text-[10px] text-[#5E6D66]">{stepStyle.label}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="space-y-6">
          {notifLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
            </div>
          ) : (
            <>
              {/* Settings Card */}
              <Card className="border-[#E8E6E1]">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Gear size={18} className="text-[#2D5A4C]" />
                      <h3 className="text-sm font-semibold text-[#1A2421]">Parametres des notifications</h3>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleTestScan}
                      disabled={testRunning}
                      className="border-[#E8E6E1]"
                      data-testid="test-scan-btn"
                    >
                      {testRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Lightning size={14} className="mr-1.5" />}
                      Lancer un scan maintenant
                    </Button>
                  </div>
                  {settings && (
                    <div className="space-y-4" data-testid="notif-settings">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium text-[#1A2421]">Changement de statut</Label>
                          <p className="text-xs text-[#5E6D66]">Email automatique quand un admin change le statut d'un dossier</p>
                        </div>
                        <Switch
                          checked={settings.status_change_enabled}
                          onCheckedChange={() => handleToggleSetting('status_change_enabled')}
                          disabled={settingsLoading}
                          data-testid="toggle-status-change"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium text-[#1A2421]">Documents manquants</Label>
                          <p className="text-xs text-[#5E6D66]">Email apres mise a jour checklist si des documents requis manquent (max 1/24h)</p>
                        </div>
                        <Switch
                          checked={settings.missing_docs_enabled}
                          onCheckedChange={() => handleToggleSetting('missing_docs_enabled')}
                          disabled={settingsLoading}
                          data-testid="toggle-missing-docs"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium text-[#1A2421]">Relance automatique (cron)</Label>
                          <p className="text-xs text-[#5E6D66]">Scan hebdomadaire avec envoi de relances pour documents manquants</p>
                        </div>
                        <Switch
                          checked={settings.cron_relance_enabled}
                          onCheckedChange={() => handleToggleSetting('cron_relance_enabled')}
                          disabled={settingsLoading}
                          data-testid="toggle-cron-relance"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Stats */}
              {notifStats && (
                <div className="grid grid-cols-3 gap-4">
                  <Card className="border-[#E8E6E1]">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="p-2 bg-emerald-100 rounded-lg"><PaperPlaneTilt size={18} className="text-emerald-600" /></div>
                      <div>
                        <p className="text-xl font-bold text-[#1A2421]" data-testid="notif-stat-sent">{notifStats.sent}</p>
                        <p className="text-xs text-[#5E6D66]">Envoyees</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-[#E8E6E1]">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg"><EnvelopeSimple size={18} className="text-blue-600" /></div>
                      <div>
                        <p className="text-xl font-bold text-[#1A2421]">{notifStats.mock}</p>
                        <p className="text-xs text-[#5E6D66]">Mode demo</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-[#E8E6E1]">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded-lg"><Warning size={18} className="text-red-500" /></div>
                      <div>
                        <p className="text-xl font-bold text-[#1A2421]">{notifStats.error}</p>
                        <p className="text-xs text-[#5E6D66]">Erreurs</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Notification History */}
              <Card className="border-[#E8E6E1]">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-[#1A2421]">Historique des notifications</h3>
                    <Button variant="ghost" size="sm" onClick={loadNotifications} className="text-[#2D5A4C]" data-testid="refresh-notif-btn">
                      Actualiser
                    </Button>
                  </div>
                  {notifications.length === 0 ? (
                    <div className="py-8 text-center">
                      <Bell size={32} className="mx-auto text-[#E8E6E1] mb-2" />
                      <p className="text-sm text-[#5E6D66]">Aucune notification envoyee</p>
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="notif-history">
                      {notifications.map((n) => (
                        <div key={n.id} className="flex items-start gap-3 p-3 rounded-lg bg-[#F9F8F5]" data-testid={`notif-${n.id}`}>
                          <div className={`p-1.5 rounded-full mt-0.5 ${
                            n.event_type === 'status_change' ? 'bg-[#2D5A4C]/10' :
                            n.event_type.includes('cron') ? 'bg-amber-100' : 'bg-blue-100'
                          }`}>
                            {n.event_type === 'status_change' ? (
                              <RocketLaunch size={14} className="text-[#2D5A4C]" />
                            ) : (
                              <FileText size={14} className={n.event_type.includes('cron') ? 'text-amber-600' : 'text-blue-600'} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <p className="text-sm font-medium text-[#1A2421] truncate">{n.subject}</p>
                              <Badge className={`text-[10px] ${NOTIF_STATUS_BADGES[n.status] || 'bg-gray-100 text-gray-500'}`}>
                                {n.status}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-[#5E6D66]">
                              <span>{n.member_name}</span>
                              <span>({n.member_email})</span>
                              <Badge className="bg-gray-100 text-gray-600 text-[10px]">
                                {EVENT_TYPE_LABELS[n.event_type] || n.event_type}
                              </Badge>
                            </div>
                            {n.detail && <p className="text-[11px] text-[#5E6D66] mt-0.5">{n.detail}</p>}
                          </div>
                          <span className="text-[11px] text-[#5E6D66] flex-shrink-0 whitespace-nowrap">
                            {n.sent_at ? new Date(n.sent_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}
